"use client"

import { Card, CardTitle } from "@/components/ui/card"
import { ExternalLink, Award, Package, CalendarDays, Leaf } from "lucide-react"
import { DirectusProduct } from "@/lib/directus"
import { getDirectusImageUrl } from "@/lib/directus"
import { SearchHighlight } from "@/components/ui/search-highlight"
import { PRODUCT_CATEGORIES } from "@/lib/product-category-slug-map"
import Image from "next/image"
import { Reveal } from "@/components/motion"
import { productGrassMatchLabel } from "@/lib/product-search"

// Cap stagger delay so a long grid doesn't have cards waiting seconds to appear.
const STAGGER_STEP = 0.04
const MAX_STAGGER_DELAY = 0.32

interface ProductCardProps {
  product: DirectusProduct
  index: number
  variant?: "default" | "featured" | "bestSeller"
  searchTerm?: string // Add search term for highlighting
  priority?: 'high' | 'medium' | 'low' // For timeline page priority-based styling
  viewMode?: "grid" | "list" // Grid or list view layout
  /** Optional callback fired when product link is clicked (for analytics tracking) */
  onProductClick?: (product: DirectusProduct) => void
  /** Liquid-glass surface (translucent + backdrop blur). Opt-in: only the
   *  /products catalog turns this on — it sits over an ambient field. Other
   *  surfaces (tool sidebars, blog callouts) stay clean white, where frosted
   *  glass over plain backgrounds would read as washed-out. */
  glass?: boolean
  /** Show the raw Amazon price. Opt-in and defaults OFF: site policy is to
   *  hide price on rec/chrome surfaces and let the CTA route users to Amazon
   *  for the live price. Only price-first surfaces (the /products catalog,
   *  head-to-head comparisons) pass showPrice. Keeping the default false means
   *  a new rec-card caller inherits the hidden default rather than re-exposing
   *  a price we'd then have to keep PAAPI-fresh + ToS-disclosed. */
  showPrice?: boolean
}

export function ProductCard({ product, index, variant = "default", searchTerm = "", priority, viewMode = "grid", onProductClick, glass = false, showPrice = false }: ProductCardProps) {
  const handleClick = () => {
    if (onProductClick) {
      onProductClick(product)
    }
  }

  const formattedPrice = showPrice && product.price
    ? `$${typeof product.price === 'string' ? product.price : product.price.toFixed(2)}`
    : null

  const grassMatchLabel = productGrassMatchLabel(product)
  const timingLabel = product.timing?.trim() || null

  // Resolve the product's category to its short tile label + glyph (e.g.
  // "Fertilizers & Soil Health" -> "Feed" + Zap). Falls back to the raw
  // category string + a generic Package glyph for any unmapped value (e.g.
  // "Smart Lawn Tech & Automation", which isn't in the 6 canonical tiles).
  const categoryMeta = product.category
    ? PRODUCT_CATEGORIES.find((c) => c.category === product.category)
    : undefined
  const CategoryIcon = categoryMeta?.icon ?? Package
  const categoryLabel = categoryMeta?.label ?? product.category

  // Surface treatment: clean white card by default; translucent liquid-glass
  // when `glass` is on (the /products catalog, which sits over an ambient field).
  const surfaceGrid = glass
    ? "bg-white/55 backdrop-blur-lg backdrop-saturate-150 border border-white/60 shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_16px_40px_-20px_rgba(15,40,25,0.20)] hover:bg-white/75 hover:border-white/85 hover:shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_28px_56px_-22px_rgba(15,40,25,0.30)]"
    : "bg-white border border-gray-200 shadow-xs hover:shadow-lg hover:border-gray-300"
  const surfaceList = glass
    ? "bg-white/55 backdrop-blur-lg backdrop-saturate-150 border border-white/60 shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_12px_30px_-18px_rgba(15,40,25,0.18)] hover:bg-white/75 hover:border-white/85"
    : "bg-white border border-gray-200 shadow-xs hover:shadow-md hover:border-gray-300"
  const footerBorder = glass ? "border-white/55" : "border-gray-100"

  // Editorial pill: quiet brand-green for both signals. Replaces the prior
  // pink/red and yellow/amber ribbons so the card reads as one cohesive family.
  const renderEditorialPill = () => {
    if (variant === "bestSeller") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-800 text-[11.5px] font-bold tracking-wide w-fit">
          <Leaf className="w-3 h-3" strokeWidth={2.2} />
          <span>Worth a look</span>
        </span>
      )
    }
    if (variant === "featured") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-800 text-[11.5px] font-bold tracking-wide w-fit">
          <Award className="w-3 h-3" strokeWidth={2.2} />
          <span>Featured</span>
        </span>
      )
    }
    return null
  }

  // Compact inline signal for the list/mini variant.
  const renderInlineSignal = () => {
    if (variant === "bestSeller") {
      return (
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-green-700 tracking-wide whitespace-nowrap">
          <Leaf className="w-3 h-3" strokeWidth={2.2} />
          Worth a look
        </span>
      )
    }
    if (variant === "featured") {
      return (
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-green-700 tracking-wide whitespace-nowrap">
          <Award className="w-3 h-3" strokeWidth={2.2} />
          Featured
        </span>
      )
    }
    return null
  }

  const getButtonStyles = () => {
    if (priority) {
      switch (priority) {
        case 'high': // Essential
          return "bg-red-600 hover:bg-red-700 focus:outline-red-500"
        case 'medium': // Recommended
          return "bg-amber-600 hover:bg-amber-700 focus:outline-amber-500"
        case 'low': // Optional
          return "bg-green-600 hover:bg-green-700 focus:outline-green-500"
        default:
          return "bg-green-600 hover:bg-green-700 focus:outline-green-500"
      }
    }
    return "bg-green-600 hover:bg-green-700 focus:outline-green-500"
  }

  const entranceDelay = Math.min(index * STAGGER_STEP, MAX_STAGGER_DELAY)
  // ─── List view: compact horizontal mini card ────────────────────────────
  if (viewMode === "list") {
    return (
      <Reveal onMount delay={entranceDelay} data-product-card="true">
        <Card className={`${surfaceList} rounded-xl hover:-translate-y-px transition-base flex flex-row items-stretch overflow-hidden group p-0 gap-0`}>
          {/* Image — square thumbnail */}
          <div className="flex-shrink-0 w-20 sm:w-28 md:w-32 p-1.5 sm:p-2 flex items-center justify-center">
            <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-linear-to-br from-gray-50 to-gray-100 border border-gray-200">
              <Image
                src={getDirectusImageUrl(product.image, 240, 240)}
                alt={product.name}
                width={240}
                height={240}
                loading="lazy"
                className="w-full h-full object-contain p-1 sm:p-2 group-hover:scale-[1.02] transition-transform duration-300"
                sizes="(max-width: 640px) 80px, (max-width: 768px) 112px, 128px"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 py-2.5 pr-1.5 sm:pr-2 flex flex-col gap-1 sm:gap-1.5 justify-center">
            {(product.category || variant !== "default") && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {product.category && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider max-w-full">
                    <CategoryIcon className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2.2} />
                    <span className="truncate">{categoryLabel}</span>
                  </span>
                )}
                {renderInlineSignal()}
              </div>
            )}
            <CardTitle className="text-sm font-bold leading-[1.3] text-gray-900 tracking-[-0.005em] line-clamp-3 sm:line-clamp-2 wrap-break-word overflow-hidden">
              <SearchHighlight
                text={product.name}
                searchTerm={searchTerm}
                className="text-sm font-bold leading-[1.3] text-gray-900 tracking-[-0.005em] wrap-break-word"
              />
            </CardTitle>
            {(grassMatchLabel || timingLabel) && (
              <div className="flex min-w-0 flex-col gap-0.5 text-[11px] leading-tight text-gray-500">
                {grassMatchLabel && (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Leaf className="h-3 w-3 flex-shrink-0 text-green-600" />
                    <span className="truncate">{grassMatchLabel}</span>
                  </span>
                )}
                {timingLabel && (
                  <span className="hidden min-w-0 items-center gap-1 sm:inline-flex">
                    <CalendarDays className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{timingLabel}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Price + CTA column with hairline divider */}
          <div data-chat-avoid className={`flex-shrink-0 py-2.5 pl-2 pr-2 sm:pl-3 sm:pr-3 flex flex-col items-end justify-center gap-2 border-l ${footerBorder}`}>
            {formattedPrice && (
              <span className="text-base font-bold text-gray-900 tracking-[-0.01em] leading-none whitespace-nowrap">
                {formattedPrice}
              </span>
            )}
            <a
              href={product.affiliate_url || '#'}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              aria-label={`Buy ${product.name} on Amazon`}
              onClick={handleClick}
              data-chat-avoid
              className={`${getButtonStyles()} text-white rounded-md px-3 py-2 min-h-[44px] font-semibold text-xs inline-flex items-center gap-1.5 whitespace-nowrap shadow-xs hover:shadow-md transition-all duration-200 focus:outline-2 focus:outline-offset-2`}
              style={{ textDecoration: 'none', color: 'white' }}
            >
              <span className="text-white">Buy</span>
              <ExternalLink className="w-3 h-3 flex-shrink-0" strokeWidth={2.2} />
            </a>
          </div>
        </Card>
      </Reveal>
    )
  }

  // ─── Grid view: editorial Wirecutter card ───────────────────────────────
  return (
    <Reveal onMount delay={entranceDelay} className="h-full" data-product-card="true">
      <Card className={`${surfaceGrid} rounded-2xl hover:-translate-y-0.5 transition-base relative h-full flex flex-col group overflow-hidden p-0 gap-0`}>
        {/* Image area with internal padding so the photo sits on a subtle bg */}
        <div className="p-3 pb-0">
          <div className="relative aspect-square rounded-xl overflow-hidden bg-linear-to-br from-gray-50 to-gray-100 border border-gray-200">
            <Image
              src={getDirectusImageUrl(product.image, 480, 480)}
              alt={product.name}
              width={480}
              height={480}
              loading="lazy"
              className="w-full h-full object-contain p-2 group-hover:scale-[1.02] transition-transform duration-300"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pt-3.5 flex-1 flex flex-col gap-2 min-w-0">
          {(product.category || variant !== "default") && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {product.category && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10.5px] font-bold uppercase tracking-wide">
                  <CategoryIcon className="w-3 h-3 flex-shrink-0" strokeWidth={2.2} />
                  {categoryLabel}
                </span>
              )}
              {renderEditorialPill()}
            </div>
          )}

          <CardTitle className="text-[15px] md:text-[16.5px] font-bold leading-[1.3] text-gray-900 tracking-[-0.01em] line-clamp-2 wrap-break-word overflow-hidden min-h-[2.6em]">
            <SearchHighlight
              text={product.name}
              searchTerm={searchTerm}
              className="text-[15px] md:text-[16.5px] font-bold leading-[1.3] text-gray-900 tracking-[-0.01em] wrap-break-word"
            />
          </CardTitle>

          {product.description && (
            <p className="text-sm leading-[1.5] text-gray-600 line-clamp-2 wrap-break-word overflow-hidden">
              <SearchHighlight
                text={product.description}
                searchTerm={searchTerm}
                className="text-sm leading-[1.5] text-gray-600 wrap-break-word"
              />
            </p>
          )}
          {(grassMatchLabel || timingLabel) && (
            <div className="mt-1 space-y-1 text-xs leading-snug text-gray-500">
              {grassMatchLabel && (
                <div className="flex items-start gap-1.5">
                  <Leaf className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-600" />
                  <span>{grassMatchLabel}</span>
                </div>
              )}
              {timingLabel && (
                <div className="flex items-start gap-1.5">
                  <CalendarDays className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                  <span>{timingLabel}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer: hairline divider, price block + CTA */}
        <div data-chat-avoid className={`mt-3.5 px-4 py-3.5 border-t ${footerBorder} flex flex-col items-stretch gap-2.5 md:flex-row md:items-center md:justify-between md:gap-3`}>
          {formattedPrice && (
            <div className="flex flex-col leading-none min-w-0">
              <span className="text-[11px] text-gray-500 font-medium tracking-wide">Price on Amazon</span>
              <span className="text-lg font-bold text-gray-900 mt-1 tracking-[-0.01em]">
                {formattedPrice}
              </span>
            </div>
          )}

          <a
            href={product.affiliate_url || '#'}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            onClick={handleClick}
            data-chat-avoid
            className={`${getButtonStyles()} text-white rounded-lg px-3 py-2 min-h-10 font-semibold text-xs inline-flex items-center justify-center gap-1.5 whitespace-nowrap shadow-xs hover:shadow-md transition-all duration-200 focus:outline-2 focus:outline-offset-2 flex-shrink-0`}
            style={{ textDecoration: 'none', color: 'white' }}
          >
            <span className="text-white">Buy on Amazon<span className="sr-only">: {product.name}</span></span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" strokeWidth={2.2} />
          </a>
        </div>
      </Card>
    </Reveal>
  )
}
